import React from "react"
import { motion } from "framer-motion"

interface ServicesSectionProps {
  laptopImages: string[]
  clinicImage: string
}

export const ServicesSection: React.FC<ServicesSectionProps> = ({
  laptopImages,
  clinicImage,
}) => {
  const [laptopImage1, laptopImage2, laptopImage3, laptopImage4] = laptopImages

  return (
    <>
      {/* Performance Stats Section */}
      <section className="py-32 bg-gradient-to-b from-purple-50 to-pink-50 relative overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.h2
            className="text-4xl md:text-6xl font-bold text-center mb-20 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            가볍고, 빠르고, 안정적이며, 편리하게
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                value: "저사양",
                label: "호환",
                desc: "최소 사양 PC에서도 작동 (Windows 11+)",
                icon: "💻",
                img: laptopImage1,
              },
              {
                value: "즉시",
                label: "재연결",
                desc: "공유기 끊김에도 안정적인 네트워크",
                icon: "⚡",
                img: laptopImage2,
              },
              {
                value: "오프라인",
                label: "사용 가능",
                desc: "인터넷 없이도 차트 작성 가능",
                icon: "📡",
                img: laptopImage3,
              },
              {
                value: "빠른",
                label: "화면 전환",
                desc: "느린 PC에서도 빠른 화면 전환",
                icon: "🚀",
                img: laptopImage4,
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all text-center relative overflow-hidden group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.05 }}
              >
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity"
                  style={{
                    backgroundImage: `url(${stat.img})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <motion.div
                  className="text-5xl mb-4 relative z-10"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.2,
                  }}
                >
                  {stat.icon}
                </motion.div>
                <div className="text-3xl font-bold text-purple-600 mb-2 relative z-10">
                  {stat.value}
                </div>
                <div className="text-lg font-semibold text-gray-700 mb-3 relative z-10">
                  {stat.label}
                </div>
                <p className="text-gray-600 text-sm relative z-10">
                  {stat.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Auto Update Section */}
      <section className="py-32 bg-white relative overflow-hidden">
        <motion.img
          src={laptopImage1}
          alt="orbit1"
          className="absolute top-20 right-10 w-24 opacity-10 rounded-lg"
          animate={{ rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        />
        <motion.img
          src={laptopImage2}
          alt="orbit2"
          className="absolute bottom-20 left-10 w-28 opacity-10 rounded-lg"
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />

        <div className="container mx-auto px-6 md:px-12">
          <motion.h2
            className="text-4xl md:text-6xl font-bold text-center mb-16 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            자동화된 업데이트
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "자동 실행",
                desc: "서버PC 매일 켜두면 자동으로 업데이트 실행",
                gradient: "from-blue-500 to-blue-700",
                img: laptopImage4,
              },
              {
                title: "이중 백업",
                desc: "자동 백업 2회. 별도 저장소로 이중 백업 추가",
                gradient: "from-cyan-500 to-cyan-700",
                img: clinicImage,
              },
              {
                title: "2초 업데이트",
                desc: "단 2초만에 완료되는 시스템 업데이트",
                gradient: "from-teal-500 to-teal-700",
                img: laptopImage3,
              },
            ].map((service, index) => (
              <motion.div
                key={index}
                className="relative group perspective-1000"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <motion.div
                  className={`bg-gradient-to-br ${service.gradient} rounded-2xl p-8 text-white shadow-xl relative overflow-hidden`}
                  whileHover={{ scale: 1.05, y: -10 }}
                >
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500"
                    style={{
                      backgroundImage: `url(${service.img})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                    <p className="text-white/90">{service.desc}</p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
